import { createAsyncThunk } from "@reduxjs/toolkit";
import api, { apiWithFormDataMethods } from "../../../utils/constants/api";
import { ApiResponse } from "../../../types/APIResponse";
import { Report } from "../../../types";
import { apiEndpoints } from "../../../utils/constants/apiEndpoints";
import { FoundReport } from "../../../types/FoundItemTypes";
import { STATUS_CODE } from "../../../utils/constants/auth";
import { AdminUser } from "../user/types";

export const getFoundReportAsyncThunk = createAsyncThunk<FoundReport, { id: string; withDeleted?: boolean }, { rejectValue: string }>("lostItem/getFoundReportAsyncThunk", async ({ id, withDeleted }, thunkAPI) => {
  try {
    let response: ApiResponse<FoundReport>;
    response = await api.get(`${apiEndpoints.found.getFoundItemByItemNumber}?itemNumber=${id}${withDeleted ? "&withDeleted=true" : ""}`);

    if (response.success) {
      return { ...response.data };
    } else {
      throw new Error(response.message || "Failed to fetch found item information");
    }
  } catch (error: any) {
    console.error("error: ", error);
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const uploadFoundDocument = createAsyncThunk<Report, { id: string | number; formData: FormData }, { rejectValue: string }>("foundReport/uploadFoundDocument", async ({ id, formData }, thunkAPI) => {
  try {
    const response: ApiResponse<Report> = await apiWithFormDataMethods.put(`${apiEndpoints.reportDetail.uploadDocument}/${id}`, formData);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to update lost item request");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const deleteFoundDocument = createAsyncThunk<string | number, { id: string | number }, { rejectValue: string }>("foundReport/deleteFoundDocument", async ({ id }, thunkAPI) => {
  try {
    const response: ApiResponse<any> = await api.delete(`${apiEndpoints.reportDetail.deleteDocument}/${id}`);
    if (response.success) {
      return id;
    } else {
      throw new Error(response.message || "Failed to delete document");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const updateFoundItemisurgent = createAsyncThunk("foundReport/updateFoundItemisurgent", async ({ id, isUrgent }: { id: string; isUrgent: boolean }, thunkAPI) => {
  try {
    const response: ApiResponse<undefined> = await api.put(`${apiEndpoints.reportDetail.updateUrgentStatus}/${id}`, { data: { isUrgent } });

    return { id, isUrgent };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const passFoundItemPendingRequest = createAsyncThunk("foundReport/passFoundItemPendingRequest", async ({ id }: { id: string }, thunkAPI) => {
  try {
    const response: ApiResponse<undefined> = await api.post(`${apiEndpoints.userRequest.passPendingRequest}/${id}`);
    return { id, isUrgent: false };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const rejectFoundItemPendingRequest = createAsyncThunk("foundReport/rejectFoundItemPendingRequest", async ({ id }: { id: string }, thunkAPI) => {
  try {
    const response: ApiResponse<undefined> = await api.put(`${apiEndpoints.userRequest.rejectPendingRequest}/${id}`);
    return { id, isUrgent: false };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const getFoundReportVersionAsyncThunk = createAsyncThunk<FoundReport, { trackingId: string; version: number; withDeleted?: boolean }, { rejectValue: string }>(
  "foundReport/getFoundReportVersionAsyncThunk",
  async ({ trackingId, version, withDeleted }, thunkAPI) => {
    try {
      const response: ApiResponse<FoundReport> = await api.get(`${apiEndpoints.found.getFoundItemInfoByVersion}?id=${trackingId}&version=${version}${withDeleted ? `&withDeleted=${withDeleted}` : ""}`);
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to fetch version information");
      }
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || "An error occurred");
    }
  }
);

export const updateFoundItemByAdmin = createAsyncThunk<FoundReport, { id: string | number; formData: FormData }, { rejectValue: string }>("foundReport/updateFoundItemByAdmin", async ({ id, formData }, thunkAPI) => {
  try {
    const response: ApiResponse<FoundReport> = await apiWithFormDataMethods.post(`${apiEndpoints.found.updateFoundItem}/${id}`, formData);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to update found item request");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const discardFoundItem = createAsyncThunk<any, { discardReason: string; foundId: string }, { rejectValue: string }>("foundReport/discardFoundItem", async ({ discardReason, foundId }, thunkAPI) => {
  try {
    const response: ApiResponse<any> = await api.put(`${apiEndpoints.found.deleteFoundItem}/${foundId}`, { data: { discardReason } });

    if (response.success && response.statusCode === STATUS_CODE.SUCCESS) {
      return response;
    } else {
      throw new Error(response.message || "Failed to send delete found item request.");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const updateFoundItemStatus = createAsyncThunk(
  "foundReport/updateFoundItemStatus",
  async ({ id, status, reason, unlinkReason, lostId, withDeleted, sendEmail }: { id: string; status: number; reason?: string; unlinkReason?: string; lostId?: string; withDeleted?: boolean; sendEmail?: boolean }, { rejectWithValue }) => {
    try {
      const response: ApiResponse<undefined> = await api.put(`${apiEndpoints.found.updateItemStatus}/${id}`, { data: { status, reason, unlinkReason, lostId, withDeleted, sendEmail } });
      if (response?.success && response?.statusCode === STATUS_CODE.SUCCESS) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to update found item status.");
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateFoundItemCaseManager = createAsyncThunk("foundReport/updateFoundItemCaseManager", async ({ id, caseManager }: { id: string; caseManager: string }, { rejectWithValue }) => {
  try {
    const response: ApiResponse<AdminUser> = await api.put(`${apiEndpoints.found.updateCaseManager}/${id}`, { data: { caseManager } });
    if (response?.success && response?.statusCode === STATUS_CODE.SUCCESS) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to update case manager.");
    }
  } catch (error: any) {
    return rejectWithValue(error?.response?.data || error.message);
  }
});
